/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Loader2,
  Users,
  Building2,
  Wallet,
  PiggyBank,
  AlertCircle,
  XCircle,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import useDocumentTitle from '../hooks/useDocumentTitle';

const PTCFinancialDashboard = () => {
  useDocumentTitle('PTC Financial Health');
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await api.get('/ptc-finance/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch financial summary:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSummary();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="p-6">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="py-8 text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-700">Failed to load financial data</p>
            <Button onClick={fetchSummary} className="mt-4">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">PTC Financial Health</h1>
          <p className="text-slate-500">Real-time financial tracking for Prestigious Travel Club</p>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={refreshing}
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* System Balance Check - Critical Indicator */}
      <Card className={`border-2 ${summary.system_balanced ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {summary.system_balanced ? (
                <div className="p-3 bg-emerald-100 rounded-full">
                  <CheckCircle className="h-8 w-8 text-emerald-600" />
                </div>
              ) : (
                <div className="p-3 bg-red-100 rounded-full animate-pulse">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
              )}
              <div>
                <p className={`text-lg font-bold ${summary.system_balanced ? 'text-emerald-800' : 'text-red-800'}`}>
                  {summary.system_balanced ? 'System Balanced' : 'Mismatch Detected'}
                </p>
                <p className={`text-sm ${summary.system_balanced ? 'text-emerald-600' : 'text-red-600'}`}>
                  {summary.balance_check_details}
                </p>
                {!summary.split_balanced && (
                  <p className="text-xs text-red-500 mt-1">
                    Split variance: ${Math.abs(summary.split_variance || 0).toFixed(2)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Badge 
                className={`text-lg px-4 py-2 ${summary.system_balanced ? 'bg-emerald-500' : 'bg-red-500'}`}
              >
                {summary.system_balanced ? 'GREEN' : 'RED'}
              </Badge>
              {summary.deals_payout_blocked > 0 && (
                <Badge variant="outline" className="text-xs border-red-400 text-red-600">
                  {summary.deals_payout_blocked} Payouts Blocked
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* v3.21-1649 · Section 1 — DEAL PERFORMANCE */}
      <div className="mt-2">
        <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Deal Performance</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-200 rounded-lg">
                <DollarSign className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Allocated Deals</p>
                <p className="text-2xl font-bold text-slate-800">{summary.total_deals}</p>
                <p className="text-[10px] text-slate-400 mt-1">In PTC allocation ledger</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2 · MONEY COLLECTED begins with Revenue Collected/Outstanding/Unallocated */}
        </div>
      </div>

      <div className="mt-4">
        <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Money Collected</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-200 rounded-lg">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Revenue Collected</p>
                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(summary.total_revenue_collected)}</p>
                <p className="text-[10px] text-slate-400 mt-1">Ledger payments received</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-200 rounded-lg">
                <Wallet className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Outstanding Balance</p>
                <p className="text-2xl font-bold text-amber-700">{formatCurrency(summary.total_outstanding_balance)}</p>
                <p className="text-[10px] text-slate-400 mt-1">Deal value not yet collected</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-200 rounded-lg">
                <PiggyBank className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Unallocated Funds</p>
                <p className="text-2xl font-bold text-blue-700">{formatCurrency(summary.total_unallocated)}</p>
                <p className="text-[10px] text-slate-400 mt-1">Collected but not yet distributed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>

      {/* v3.21-1649 · Section 3 — OBLIGATIONS / PAYABLES.
          Duplicate 'Partner Costs' + 'Commissions Owed' cards removed —
          they were the same dollars as Total Partner Payables +
          Total Commission Payables (both derived from the same
          `partner_costs_owed` / `commissions_owed` figures — Owed = paid + pending). */}
      <div className="mt-4">
        <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Obligations · Payables</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-2 border-red-300 bg-gradient-to-br from-red-50 to-orange-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="h-5 w-5 text-red-600" />
              <p className="text-sm font-medium text-red-700">Partner Payables</p>
            </div>
            <p className="text-2xl font-bold text-red-800">{formatCurrency(summary.total_partner_payable)}</p>
            <p className="text-xs text-red-600 mt-1">Attorney &amp; partner fees · pending + paid</p>
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5 text-purple-600" />
              <p className="text-sm font-medium text-purple-700">Commission Payables</p>
            </div>
            <p className="text-2xl font-bold text-purple-800">{formatCurrency(summary.total_commission_payable)}</p>
            <p className="text-xs text-purple-600 mt-1">Sales rep commissions · pending + paid</p>
          </CardContent>
        </Card>
        </div>
      </div>

      {/* v3.21-1649 · Section 4 — NET COMPANY POSITION.
          `Total PTC Revenue` renamed → `Gross PTC Share` (what PTC keeps
          BEFORE recognising the payables above; formula: Collected −
          Partner Payable − Commission Payable) so it can no longer be
          confused with `Net Profit (Actual)` (which is derived from
          Collected − Commissions Owed − Partner Costs Owed and IS the
          same dollars as Gross PTC Share while all obligations equal the
          collected split).  Kept as two labelled views so the operator
          can see the split-based figure and the profit-based figure
          alongside — they will match when the split is balanced. */}
      <div className="mt-4">
        <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Net Company Position</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-5 w-5 text-emerald-600" />
              <p className="text-sm font-medium text-emerald-700">Gross PTC Share</p>
            </div>
            <p className="text-2xl font-bold text-emerald-800">{formatCurrency(summary.total_ptc_receivable)}</p>
            <p className="text-xs text-emerald-600 mt-1">Collected − Partner − Commission (split view)</p>
          </CardContent>
        </Card>

        <Card className={`border-2 ${summary.net_profit_actual >= 0 ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              {summary.net_profit_actual >= 0 ? (
                <TrendingUp className="h-4 w-4 text-emerald-500" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-500" />
              )}
              <p className="text-sm text-slate-500">Net Profit · Actual</p>
            </div>
            <p className={`text-2xl font-bold ${summary.net_profit_actual >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              {formatCurrency(summary.net_profit_actual)}
            </p>
            <p className="text-xs text-slate-400 mt-1">Collected − Commission Owed − Partner Cost Owed</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-4 w-4 text-slate-500" />
              <p className="text-sm text-slate-500">Net Profit · Projected</p>
            </div>
            <p className="text-2xl font-bold text-slate-700">{formatCurrency(summary.net_profit_projected)}</p>
            <p className="text-xs text-slate-400 mt-1">Total Deal Value − Projected Commission − Partner Cost</p>
          </CardContent>
        </Card>
        </div>
      </div>

      {/* v3.21-1649 · Section 5 — EXCEPTIONS REQUIRING ACTION heading */}
      <div className="mt-4">
        <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Exceptions Requiring Action</h2>
      </div>

      {/* Deal Alerts */}
      {summary.deals_with_alerts > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-amber-800">
              <AlertCircle className="h-5 w-5" />
              Flagged Deals ({summary.deals_with_alerts})
              {summary.deals_payout_blocked > 0 && (
                <Badge variant="destructive" className="ml-2 text-xs">
                  {summary.deals_payout_blocked} Payout Blocked
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-amber-700 mb-4">
              The following deals have financial alerts that need attention:
            </p>
            <div className="space-y-2">
              {summary.flagged_deals && summary.flagged_deals.map((deal) => (
                <div 
                  key={deal.deal_id}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/clients/${deal.deal_id}`)}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-800">{deal.client_name}</p>
                      {deal.payout_eligible === false && (
                        <Badge variant="destructive" className="text-[10px]">
                          Payout Blocked
                        </Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 font-mono mt-0.5" data-testid="flagged-deal-id">
                      Deal ID: {deal.deal_id}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {deal.alerts && deal.alerts.map((alert, i) => (
                        <Badge key={`${alert}-${i}`} variant="outline" className="text-xs text-amber-700 border-amber-300">
                          {alert}
                        </Badge>
                      ))}
                      {!deal.is_balanced && (
                        <Badge variant="destructive" className="text-xs">
                          Imbalance: ${Math.abs(deal.imbalance_amount).toFixed(2)}
                        </Badge>
                      )}
                    </div>
                    {deal.payout_block_reasons?.length > 0 && (
                      <p className="text-[10px] text-red-600 mt-1">
                        Blocked: {deal.payout_block_reasons.join(', ')}
                      </p>
                    )}
                  </div>
                  <ArrowRight className="h-5 w-5 text-amber-500" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Financial Rules Summary */}
      <Card className="bg-slate-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-slate-700">Financial Rules & Validation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="p-3 bg-white rounded-lg border border-emerald-200">
              <p className="font-medium text-emerald-700 mb-1">Split Validation Rule</p>
              <p className="text-slate-600 font-mono text-xs">
                Total Collected = PTC + Partner + Commission
              </p>
              <p className="text-xs text-slate-400 mt-1">Must balance before payout approval</p>
            </div>
            <div className="p-3 bg-white rounded-lg border border-blue-200">
              <p className="font-medium text-blue-700 mb-1">System Balance Rule</p>
              <p className="text-slate-600 font-mono text-xs">
                Total Collected = Allocated + Unallocated
              </p>
              <p className="text-xs text-slate-400 mt-1">Ensures all funds are tracked</p>
            </div>
            <div className="p-3 bg-white rounded-lg border border-purple-200">
              <p className="font-medium text-purple-700 mb-1">True Profit Calculation</p>
              <p className="text-slate-600 font-mono text-xs">
                Net Profit = Collected - Commission Owed - Partner Costs
              </p>
              <p className="text-xs text-slate-400 mt-1">Accounts for all pending obligations</p>
            </div>
            <div className="p-3 bg-white rounded-lg border border-red-200">
              <p className="font-medium text-red-700 mb-1">Hard Payout Lock</p>
              <p className="text-slate-600 text-xs">
                Payouts blocked if: missing commission, unallocated funds, split mismatch, or imbalanced ledger
              </p>
              <p className="text-xs text-slate-400 mt-1">Backend enforced - cannot be bypassed</p>
            </div>
            <div className="p-3 bg-white rounded-lg border">
              <p className="font-medium text-slate-700 mb-1">Commission Rate</p>
              <p className="text-slate-500">15% of collected amount</p>
            </div>
            <div className="p-3 bg-white rounded-lg border">
              <p className="font-medium text-slate-700 mb-1">Scope</p>
              <p className="text-slate-500">Prestigious Travel Club deals only</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PTCFinancialDashboard;
